/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to WARRANTIES of merchantability or fitness for a particular purpose.
 */

import { config as dotenvConfig } from 'dotenv';
import fs from 'fs';
import path from 'path';
import { fileURLToPath } from 'url';

// Cache for loaded environment to avoid repeated file reads
let _envLoaded = false;
let _secretsLoaded = false;

// ============================================================================
// UNIVERSAL ENVIRONMENT VARIABLE ACCESS
// ============================================================================
// 
// These functions provide a standardized interface for accessing environment
// variables across all contexts: browser, Node.js server, and Electron app.
//
// Environment Variable Naming Conventions:
// - Browser (Vite): VITE_* prefix (e.g., VITE_DISABLE_AUTH, VITE_REMOTE_AUTH_MODE)
// - Server (Node): No prefix (e.g., DISABLE_AUTH, REMOTE_AUTH_MODE)
// - Electron: Injected via window.__REMOTE_AUTH_CONFIG__ or window.__FIREBASE_CONFIG__
//
// The getEnv() function automatically handles these differences:
// - In browser: Checks Electron config first, then import.meta.env.VITE_*
// - In Node.js: Checks process.env directly
// ============================================================================

/**
 * Check if we're running in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Check if we're running on the server (Node.js)
 */
export function isServer(): boolean {
  return typeof window === 'undefined';
}

/**
 * Get an environment variable value that works across all contexts.
 * 
 * This function automatically handles the different env access patterns:
 * - Browser: Checks Electron-injected config, then import.meta.env.VITE_*
 * - Node.js: Checks process.env directly
 * 
 * @param key - The environment variable name WITHOUT the VITE_ prefix
 *              (e.g., "DISABLE_AUTH" not "VITE_DISABLE_AUTH")
 * @param defaultValue - Optional default value if not found
 * @returns The environment variable value or the default value
 * 
 * @example
 * // Works in both browser and server
 * const authDisabled = getEnv('DISABLE_AUTH');
 * const masterUrl = getEnv('VELOCITY_MASTER_SERVER_URL', 'https://auth.velocityapp.ai');
 */
export function getEnv(key: string, defaultValue?: string): string | undefined {
  if (isBrowser()) {
    // Browser context: Check multiple sources in priority order
    
    // 1. Check Electron-injected config (set by Velocity server in Electron mode)
    const electronConfig = (window as any).__REMOTE_AUTH_CONFIG__;
    if (electronConfig) {
      const electronValue = electronConfig[`VITE_${key}`];
      if (electronValue !== undefined) {
        return electronValue;
      }
    }
    
    // 2. Check Firebase config (for Firebase-specific settings)
    const firebaseConfig = (window as any).__FIREBASE_CONFIG__;
    if (firebaseConfig) {
      const firebaseValue = firebaseConfig[key];
      if (firebaseValue !== undefined) {
        return firebaseValue;
      }
    }
    
    // 3. Check Vite environment variables (for web builds)
    // Use optional chaining since import.meta.env may not exist in all contexts
    const viteEnv = (import.meta as any)?.env;
    if (viteEnv) {
      // Check with VITE_ prefix first (browser convention)
      const viteValue = viteEnv[`VITE_${key}`];
      if (viteValue !== undefined) {
        return viteValue;
      }
      // Also check without prefix for MODE, NODE_ENV, etc.
      const directValue = viteEnv[key];
      if (directValue !== undefined) {
        return directValue;
      }
    }
    
    return defaultValue;
  } else {
    // Node.js context: Load environment and check process.env
    loadEnvironmentVariables();
    return process.env[key] ?? defaultValue;
  }
}

/**
 * Get an environment variable as a boolean.
 * 
 * Recognizes 'true', '1', 'yes' as true (case-insensitive).
 * All other values (including undefined) are false unless a default is specified.
 * 
 * @param key - The environment variable name WITHOUT the VITE_ prefix
 * @param defaultValue - Optional default value if not found (defaults to false)
 * @returns Boolean value of the environment variable
 * 
 * @example
 * const authDisabled = getEnvBoolean('DISABLE_AUTH'); // false if not set
 * const remoteAuth = getEnvBoolean('REMOTE_AUTH_MODE', true); // true if not set
 */
export function getEnvBoolean(key: string, defaultValue: boolean = false): boolean {
  const value = getEnv(key);
  if (value === undefined) {
    return defaultValue;
  }
  const normalized = value.toLowerCase().trim();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

/**
 * Get the current Node environment (development, production, or test).
 * 
 * @returns The current NODE_ENV value, defaults to 'development'
 */
export function getNodeEnv(): 'development' | 'production' | 'test' {
  if (isBrowser()) {
    // In browser, check MODE first (Vite convention), then NODE_ENV
    const viteEnv = (import.meta as any)?.env;
    const mode = viteEnv?.MODE || viteEnv?.NODE_ENV || 'development';
    return mode as 'development' | 'production' | 'test';
  } else {
    return (process.env.NODE_ENV || 'development') as 'development' | 'production' | 'test';
  }
}

/**
 * Check if we're running in development mode
 */
export function isDevelopment(): boolean {
  return getNodeEnv() === 'development';
}

/**
 * Check if we're running in production mode
 */
export function isProduction(): boolean {
  return getNodeEnv() === 'production';
}

/**
 * Check if we're running in test mode
 */
export function isTest(): boolean {
  return getNodeEnv() === 'test';
}

/**
 * Load environment variables from .env files in order of precedence:
 * 1. .env.local (highest priority, git-ignored)
 * 2. .env.[NODE_ENV] (environment-specific)
 * 3. .env (default, committed to git)
 * 
 * This function can be called multiple times safely - it will only load once.
 */
export function loadEnvironmentVariables(projectRoot?: string): void {
  try {
    if (_envLoaded) {
      return; // Already loaded, skip
    }

    const nodeEnv = process.env.NODE_ENV || 'development';
    console.log(`Loading environment configuration for: ${nodeEnv}`);

    // Determine project root
    let rootPath: string;
    if (projectRoot) {
      rootPath = projectRoot;
      console.log(`Using provided project root: ${rootPath}`);
    } else if (process.env.PROJECT_ROOT) {
      // Check for PROJECT_ROOT environment variable (useful for Electron apps)
      rootPath = process.env.PROJECT_ROOT;
      console.log(`Using PROJECT_ROOT from environment: ${rootPath}`);
    } else {
      console.log('Finding project root by looking for package.json...');
      // Try to find project root by looking for package.json
      let currentPath = process.cwd();
      console.log(`Starting search from: ${currentPath}`);
      
      while (currentPath !== path.dirname(currentPath)) {
        const packageJsonPath = path.join(currentPath, 'package.json');
        console.log(`Checking for package.json at: ${packageJsonPath}`);
        
        if (fs.existsSync(packageJsonPath)) {
          rootPath = currentPath;
          console.log(`Found project root: ${rootPath}`);
          break;
        }
        currentPath = path.dirname(currentPath);
      }
      
      if (!rootPath!) {
        rootPath = process.cwd(); // Fallback to current directory
        console.log(`Using fallback project root: ${rootPath}`);
      }
    }

    const envFiles = [
      '.env.local',
      `.env.${nodeEnv}`,
      '.env'
    ];

    console.log(`Searching for environment files in: ${rootPath}`);

    envFiles.forEach(envFile => {
      try {
        const envPath = path.join(rootPath, envFile);
        console.log(`Checking for env file: ${envPath}`);
        
        if (fs.existsSync(envPath)) {
          console.log(`Loading environment variables from: ${envFile}`);
          dotenvConfig({ path: envPath });
          console.log(`Successfully loaded: ${envFile}`);
        } else {
          console.log(`Environment file not found: ${envFile}`);
        }
      } catch (error: any) {
        console.error(`Error loading environment file ${envFile}:`, {
          message: error.message,
          stack: error.stack,
          code: error.code
        });
        throw new Error(`Failed to load environment file ${envFile}: ${error.message}`);
      }
    });

    _envLoaded = true;
    console.log('Environment variables loaded successfully');
    
    // Also load secrets if they exist
    console.log('Loading secrets...');
    loadSecrets(rootPath);
    console.log('Environment initialization complete');
    
  } catch (error: any) {
    console.error('CRITICAL ERROR in loadEnvironmentVariables:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
      code: error.code,
      cwd: typeof process !== 'undefined' ? process.cwd() : 'browser',
      nodeEnv: typeof process !== 'undefined' ? process.env.NODE_ENV : 'browser'
    });
    throw error; // Re-throw to surface the error
  }
}

/**
 * Load secrets from .env.secrets file
 * This file contains sensitive information like API keys that shouldn't be committed to git
 */
export function loadSecrets(projectRoot?: string): void {
  try {
    if (_secretsLoaded) {
      console.log('Secrets already loaded, skipping');
      return; // Already loaded, skip
    }
    
    // Determine project root
    let rootPath: string;
    if (projectRoot) {
      rootPath = projectRoot;
      console.log(`Using provided project root for secrets: ${rootPath}`);
    } else if (process.env.PROJECT_ROOT) {
      // Check for PROJECT_ROOT environment variable (useful for Electron apps)
      rootPath = process.env.PROJECT_ROOT;
      console.log(`Using PROJECT_ROOT from environment for secrets: ${rootPath}`);
    } else {
      console.log('Finding project root for secrets...');
      // Try to find project root by looking for package.json
      let currentPath = process.cwd();
      while (currentPath !== path.dirname(currentPath)) {
        if (fs.existsSync(path.join(currentPath, 'package.json'))) {
          rootPath = currentPath;
          break;
        }
        currentPath = path.dirname(currentPath);
      }
      
      if (!rootPath!) {
        rootPath = process.cwd(); // Fallback to current directory
      }
      console.log(`Using project root for secrets: ${rootPath}`);
    }
    
    const secretsPath = path.join(rootPath, '.env.secrets');
    console.log(`Checking for secrets file: ${secretsPath}`);
    
    if (fs.existsSync(secretsPath)) {
      console.log('Loading secrets from .env.secrets');
      dotenvConfig({ path: secretsPath });
      console.log('Successfully loaded secrets from .env.secrets');
    } else {
      console.log('No .env.secrets file found (this is optional)');
    }
    
    // Handle GCP_KEY_FILE_PATH: if present, read the file and set GCS_KEY_FILE_CONTENT
    const gcpKeyFilePath = process.env.GCP_KEY_FILE_PATH;
    if (gcpKeyFilePath) {
      console.log(`GCP_KEY_FILE_PATH found: ${gcpKeyFilePath}`);
      try {
        // Resolve path relative to project root if it's not absolute
        const keyFilePath = path.isAbsolute(gcpKeyFilePath) 
          ? gcpKeyFilePath 
          : path.join(rootPath, gcpKeyFilePath);
        
        console.log(`Reading GCP service account key from: ${keyFilePath}`);
        
        if (fs.existsSync(keyFilePath)) {
          const keyFileContent = fs.readFileSync(keyFilePath, 'utf8');
          // Set the GCS_KEY_FILE_CONTENT environment variable
          process.env.GCS_KEY_FILE_CONTENT = keyFileContent;
          console.log('Successfully loaded GCP service account key from file');
        } else {
          console.warn(`GCP key file not found at path: ${keyFilePath}`);
        }
      } catch (error: any) {
        console.error('Error reading GCP key file:', {
          message: error.message,
          path: gcpKeyFilePath
        });
      }
    }
    
    _secretsLoaded = true;
    console.log('Secrets loading complete');
    
  } catch (error: any) {
    console.error('ERROR in loadSecrets:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
      code: error.code
    });
    // Don't throw here since secrets are optional
    _secretsLoaded = true; // Mark as loaded to prevent retry loops
  }
}

/**
 * Get a secret from environment variables
 * @param key The environment variable key
 * @returns The secret value or undefined if not found
 */
export function getSecret(key: string): string | undefined {
  loadEnvironmentVariables(); // This will also load secrets
  return process.env[key];
}

/**
 * Get database type from environment variables, loading them if necessary
 */
export function getDatabaseType(): 'sqlite' | 'postgresql' {
  loadEnvironmentVariables();
  return (process.env.DB_TYPE || 'sqlite') as 'sqlite' | 'postgresql';
}

/**
 * Get database URL from environment variables, loading them if necessary
 */
export function getDatabaseUrl(): string | undefined {
  loadEnvironmentVariables();
  return process.env.DATABASE_URL;
}

/**
 * Get SQLite database path from environment variables, loading them if necessary
 */
export function getSqliteDbPath(): string {
  loadEnvironmentVariables();
  return process.env.SQLITE_DB_PATH || './data/skillshop.sqlite';
}

/**
 * Get database pool max from environment variables, loading them if necessary
 */
export function getDatabasePoolMax(): number {
  loadEnvironmentVariables();
  return parseInt(process.env.DB_POOL_MAX || '10');
}

/**
 * Check if authentication is disabled (only works in development)
 * 
 * IMPORTANT: In browser context, we must use the literal expression
 * `import.meta.env.VITE_DISABLE_AUTH` for Vite's compile-time replacement
 * to work. Dynamic property access like `env['VITE_DISABLE_AUTH']` or
 * `env?.VITE_DISABLE_AUTH` through a variable won't be replaced.
 */
export function isAuthDisabled(): boolean {
  // Only allow auth to be disabled in development
  if (!isDevelopment()) {
    return false;
  }
  
  if (isBrowser()) {
    // MUST use literal import.meta.env.VITE_DISABLE_AUTH for Vite replacement
    // @ts-ignore - Vite replaces this at compile time
    const value = import.meta.env.VITE_DISABLE_AUTH;
    if (value === undefined || value === '') {
      return false;
    }
    const normalized = String(value).toLowerCase().trim();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  } else {
    // Server-side can use the generic getEnv function
    return getEnvBoolean('DISABLE_AUTH');
  }
}

/**
 * Reset the environment loading cache (useful for testing)
 */
export function resetEnvironmentCache(): void {
  _envLoaded = false;
  _secretsLoaded = false;
}

/**
 * Check if we're running in Electron (desktop app mode)
 * 
 * Desktop apps have different expectations for authentication persistence:
 * - Users expect to log in once and stay logged in indefinitely
 * - Token expiration should be handled transparently
 * - Users should only see signin after explicit logout or account deactivation
 * 
 * @returns true if running in Electron desktop app
 */
export function isElectronApp(): boolean {
  if (isServer()) {
    // In Node.js, check for ELECTRON_RUN environment variable
    return getEnvBoolean('ELECTRON_RUN');
  }
  
  // In browser, check for Electron-specific indicators
  // 1. Check for injected Electron config (set by Velocity server in Electron mode)
  const hasElectronConfig = !!(window as any).__FIREBASE_CONFIG__ || !!(window as any).__REMOTE_AUTH_CONFIG__;
  
  // 2. Check user agent for Electron
  const hasElectronUserAgent = navigator?.userAgent?.includes('Electron');
  
  // 3. Check for Electron-specific APIs
  const hasElectronAPIs = typeof (window as any).electronAPI !== 'undefined';
  
  return hasElectronConfig || hasElectronUserAgent || hasElectronAPIs;
}

/**
 * Get the project root directory for file operations.
 * Uses PROJECT_ROOT env var if set (for Electron), otherwise uses cwd.
 * 
 * This is a simple utility for tools and services that need the project root
 * without the full environment loading logic.
 * 
 * Note: This function is server-side only (requires process.cwd())
 */
export function getProjectRoot(): string {
  if (isServer()) {
    return process.env.PROJECT_ROOT || process.cwd();
  }
  // In browser, return empty string (should not be called from browser)
  console.warn('getProjectRoot() called from browser context - this is a server-only function');
  return '';
}